const API_BASE = "https://api.globalwineinstitute.org";
const API_KEY = "f3bb4afa55070cc052d1b888d4f7465a";
const THEMES_ENDPOINT = `${API_BASE}/themes`;

let rawThemesCache = null;
let rawThemesPromise = null;
let normalizedThemesCache = null;
let normalizedThemesPromise = null;

const sanitizeThemeId = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }
  return null;
};

const normalizeThemeItem = (item) => {
  if (!item) return null;
  if (typeof item === "string") {
    const id = sanitizeThemeId(item);
    return id ? { id, label: item.trim(), original: { value: item } } : null;
  }
  const idCandidates = [
    item.topic_id,
    item.theme_id,
    item.themeId,
    item.topicId,
    item.id,
    item.value,
    item.key,
    item.slug,
  ];
  const id =
    idCandidates
      .map((candidate) => sanitizeThemeId(candidate))
      .find((candidate) => candidate != null) ?? null;
  if (!id) return null;
  const labelCandidate = item.name ?? item.description ?? item.id ?? id;
  const label =
    typeof labelCandidate === "string" && labelCandidate.trim()
      ? labelCandidate.trim()
      : String(labelCandidate ?? id);
  const name = item.name ?? item.description ?? label;
  return { id, label, name, original: item };
};

const fetchRawThemes = async () => {
  if (rawThemesCache) return rawThemesCache;
  if (!rawThemesPromise) {
    rawThemesPromise = fetch(THEMES_ENDPOINT, {
      headers: API_KEY ? { "X-API-KEY": API_KEY } : {},
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch themes (${res.status} ${res.statusText})`
          );
        }
        return res.json();
      })
      .then((data) => {
        const payload = Array.isArray(data) ? data : [];
        rawThemesCache = payload;
        return payload;
      })
      .catch((err) => {
        rawThemesPromise = null;
        throw err;
      });
  }
  return rawThemesPromise;
};

const fetchNormalizedThemes = async () => {
  if (normalizedThemesCache) return normalizedThemesCache;
  if (!normalizedThemesPromise) {
    normalizedThemesPromise = fetchRawThemes()
      .then((list) =>
        list
          .map((item) => normalizeThemeItem(item))
          .filter((entry) => entry != null)
      )
      .then((normalized) => {
        normalizedThemesCache = normalized;
        return normalized;
      })
      .catch((err) => {
        normalizedThemesPromise = null;
        throw err;
      });
  }
  return normalizedThemesPromise;
};

export const getThemes = async ({ normalized = true } = {}) => {
  return normalized ? fetchNormalizedThemes() : fetchRawThemes();
};

export const refreshThemes = async ({ normalized = true } = {}) => {
  clearThemesCache();
  return getThemes({ normalized });
};

export const clearThemesCache = () => {
  rawThemesCache = null;
  normalizedThemesCache = null;
  rawThemesPromise = null;
  normalizedThemesPromise = null;
};

export const getThemeById = async (id) => {
  const normalized = await getThemes();
  return normalized.find((item) => item.id === sanitizeThemeId(id)) || null;
};

export const themeServiceUtils = {
  sanitizeThemeId,
  normalizeThemeItem,
};

export default {
  getThemes,
  refreshThemes,
  clearThemesCache,
  getThemeById,
  themeServiceUtils,
};
